import React, { useState } from 'react'

/**
 * SuggestionConfirmModal - Shows inferred relationship suggestions after adding a family member
 *
 * When a user adds a family member, the system may detect additional relationships
 * that should be created (e.g., adding a mother should also connect her to siblings).
 * This modal presents those suggestions with checkboxes for user confirmation.
 */
function SuggestionConfirmModal({
  suggestions = [],
  newPersonName = '',
  onAccept,
  onSkip,
  isLoading = false
}) {
  // Initialize all suggestions as selected by default
  const [selected, setSelected] = useState(() =>
    suggestions.reduce((acc, s) => ({ ...acc, [s.id]: true }), {})
  )

  const handleToggle = (id) => {
    setSelected(prev => ({ ...prev, [id]: !prev[id] }))
  }

  const handleApply = async () => {
    const toAccept = Object.entries(selected)
      .filter(([_, checked]) => checked)
      .map(([id]) => id)
    const toReject = Object.entries(selected)
      .filter(([_, checked]) => !checked)
      .map(([id]) => id)

    await onAccept({ accept: toAccept, reject: toReject })
  }

  const selectedCount = Object.values(selected).filter(Boolean).length

  if (!suggestions || suggestions.length === 0) {
    return null
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {newPersonName} Added!
          </h2>
          <p className="text-gray-600">
            We found some family connections that might also apply:
          </p>
        </div>

        {/* Suggestions List */}
        <div className="space-y-3 mb-6">
          {suggestions.map(suggestion => (
            <label
              key={suggestion.id}
              className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                selected[suggestion.id]
                  ? 'border-[#556B2F] bg-green-50'
                  : 'border-gray-200 bg-gray-50 hover:border-gray-300'
              }`}
            >
              <input
                type="checkbox"
                checked={selected[suggestion.id] || false}
                onChange={() => handleToggle(suggestion.id)}
                className="w-5 h-5 mt-1 text-[#556B2F] border-2 border-gray-300 rounded focus:ring-[#556B2F]"
              />
              <div className="flex-1">
                <div className="font-semibold text-gray-900">
                  {/* Relationship semantic: person1's {type} is person2 */}
                  {/* Display: "person2 is person1's type" (e.g., "Sangeeta is Sonali's Mother") */}
                  {suggestion.person2?.name || 'Unknown'} is {suggestion.person1?.name || 'Unknown'}'s {suggestion.relationship_type}
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  {suggestion.explanation}
                </div>
                {suggestion.confidence < 0.8 && (
                  <div className="text-xs text-amber-600 mt-1">
                    Lower confidence - please verify
                  </div>
                )}
              </div>
            </label>
          ))}
        </div>

        {/* Info text */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> You can uncheck any suggestions that don't apply
            (e.g., for half-siblings or step-parents).
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={handleApply}
            disabled={isLoading}
            className="flex-1 py-3 px-6 rounded-xl font-bold text-white transition-all disabled:opacity-50"
            style={{ backgroundColor: '#556B2F' }}
          >
            {isLoading ? 'Applying...' : `Apply ${selectedCount > 0 ? `(${selectedCount})` : 'Selected'}`}
          </button>
          <button
            onClick={onSkip}
            disabled={isLoading}
            className="py-3 px-6 rounded-xl font-bold bg-gray-200 text-gray-700 hover:bg-gray-300 transition-all disabled:opacity-50"
          >
            Skip
          </button>
        </div>
      </div>
    </div>
  )
}

export default SuggestionConfirmModal
